import { useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';

import garantiasApi from '../../../api/garantiasApi';
import { getErrorMessage } from '../../../utils/errorHandler';

const MAX_IMAGES = 3;

// Evidencia fotografica de una garantia ya creada: subir (hasta 3, jpg/jpeg/png) y eliminar.
// Cada accion es inmediata (no forma parte del envio del formulario principal), por eso avisa
// via `onChanged` para refrescar.
export default function GarantiaImagesManager({ garantiaId, images, onChanged }) {
  const { enqueueSnackbar } = useSnackbar();
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    setUploading(true);
    try {
      await garantiasApi.uploadGarantiaImage(garantiaId, file);
      onChanged();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo subir la imagen.'), { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (imageId) => {
    setBusyId(imageId);
    try {
      await garantiasApi.deleteGarantiaImage(garantiaId, imageId);
      onChanged();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo eliminar la imagen.'), { variant: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Stack spacing={1}>
      <Stack sx={{ flexWrap: 'wrap', gap: 1.5 }} direction="row">
        {images.map((image) => (
          <Box
            key={image.id}
            sx={{
              position: 'relative',
              width: 96,
              height: 96,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <Box
              component="img"
              src={image.url}
              alt=""
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
            <Tooltip title="Eliminar">
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleDelete(image.id)}
                  disabled={busyId === image.id}
                  sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'background.paper' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ))}

        {images.length < MAX_IMAGES && (
          <Box
            component="label"
            sx={{
              width: 96,
              height: 96,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              cursor: uploading ? 'default' : 'pointer',
              color: 'text.secondary',
            }}
          >
            <AddPhotoAlternateOutlinedIcon />
            <Typography variant="caption">{uploading ? 'Subiendo...' : 'Subir'}</Typography>
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              hidden
              disabled={uploading}
              onChange={handleFileChange}
            />
          </Box>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {images.length} de {MAX_IMAGES} imágenes (jpg, jpeg o png).
      </Typography>
    </Stack>
  );
}

GarantiaImagesManager.propTypes = {
  garantiaId: PropTypes.number.isRequired,
  images: PropTypes.array.isRequired,
  onChanged: PropTypes.func.isRequired,
};
